import { useEffect, useState } from 'react';
import { FUND_CURRENCIES, type AltBatchView, type AltField, type AltFileEdit, type AltFileView, type AltWarning } from '@agio/core/domain';
/** Plain digits with commas — no direction marks, so what is typed back parses. */
const show = (v: number | null) => (v === null ? '' : v.toLocaleString('en-US', { maximumFractionDigits: 2 }));
import { altApi } from './altApi.js';

/**
 * What was read from each quarterly report, next to the words it was read from.
 *
 * One card per document. Each figure sits in its own row with the quote and
 * the place in the document it came from, so checking it is reading across a
 * line — not hunting through a PDF. Every value is editable; editing one takes
 * back the confirmation, because what was confirmed was the old value.
 */
export function AltReview({
  batch,
  busy,
  onEdit,
  onRemove,
}: {
  batch: AltBatchView;
  busy: boolean;
  onEdit: (fileId: string, edit: AltFileEdit, confirm?: boolean) => void;
  onRemove: (fileId: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4">
      {batch.files.map((f) => (
        <FileCard key={f.file_id} file={f} batch={batch} busy={busy} onEdit={onEdit} onRemove={onRemove} />
      ))}
    </div>
  );
}

const NEW = '__new__';

/** Israeli convention, and never wrapped mid-date. */
export const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

function FileCard({ file, batch, busy, onEdit, onRemove }: {
  file: AltFileView;
  batch: AltBatchView;
  busy: boolean;
  onEdit: (fileId: string, edit: AltFileEdit, confirm?: boolean) => void;
  onRemove: (fileId: string) => void;
}) {
  const blocking = file.warnings.some((w) => w.level === 'error' && w.code !== 'unconfirmed');
  const confirmed = !!file.confirmed_by;
  const x = file.extraction;
  const edit = (e: AltFileEdit) => onEdit(file.file_id, e);

  const openDocument = async () => {
    const blob = await altApi.document(file.file_id);
    window.open(URL.createObjectURL(blob), '_blank', 'noopener');
  };

  return (
    <div className={`min-w-0 rounded-xl border bg-card p-4 ${blocking ? 'border-destructive/50' : confirmed ? 'border-success/50' : 'border-border'}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-all font-medium">{file.filename}</span>
        <span className="flex flex-wrap gap-1.5">
          {file.has_document && (
            <button type="button" disabled={busy} onClick={() => void openDocument()}
              className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50">
              פתיחת המסמך
            </button>
          )}
          <button type="button" aria-label={`הסרת ${file.filename}`} disabled={busy} onClick={() => onRemove(file.file_id)}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50">
            הסרה
          </button>
        </span>
      </div>

      {file.status !== 'extracted' ? (
        <div className="grid gap-2">
          {file.warnings.map((w) => <Notice key={w.code + w.message} warning={w} />)}
          {x && <p className="text-xs text-muted-foreground">מה שנקרא: {x.investment_name ?? '—'} · {x.as_of ?? '—'} · {x.nav ?? '—'} {x.currency ?? ''}</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">לאיזו השקעה זה שייך?</span>
            <InvestmentPicker file={file} batch={batch} busy={busy} onEdit={edit} />
          </label>

          {/* Each figure beside its quote. On a phone the "where" column is left out and the rest scrolls inside its own box if needed. */}
          <div className="overflow-x-auto rounded-lg border border-border/60">
            <table className="w-full min-w-[18rem] text-sm sm:min-w-[34rem]">
              <thead>
                <tr className="border-b border-border text-start text-xs text-muted-foreground">
                  <th className="px-3 py-2 text-start font-normal">שדה</th>
                  <th className="px-3 py-2 text-start font-normal">ערך לפרסום</th>
                  <th className="px-3 py-2 text-start font-normal">כפי שמופיע במסמך</th>
                  <th className="hidden px-3 py-2 text-start font-normal sm:table-cell">איפה</th>
                </tr>
              </thead>
              <tbody>
                <Row label="תאריך הדוח" field="as_of" file={file}>
                  <input type="date" defaultValue={file.as_of ?? ''} disabled={busy} key={`d${file.as_of}`}
                    aria-label="תאריך הדוח"
                    onBlur={(e) => e.target.value && e.target.value !== file.as_of && edit({ as_of: e.target.value })}
                    className="w-32 sm:w-40 rounded-md border border-input bg-popover px-2 py-1 tabular-nums" />
                </Row>
                <Row label="שווי (NAV)" field="nav" file={file}>
                  <NumberBox value={file.nav} busy={busy} label="שווי" onSave={(v) => v !== null && edit({ nav: v })} />
                </Row>
                <Row label="קריאות לכסף ברבעון" field="capital_calls" file={file}>
                  <NumberBox value={file.capital_calls} busy={busy} label="קריאות לכסף" onSave={(v) => edit({ capital_calls: v })} />
                </Row>
                <Row label="חלוקות ברבעון" field="distributions" file={file}>
                  <NumberBox value={file.distributions} busy={busy} label="חלוקות" onSave={(v) => edit({ distributions: v })} />
                </Row>
                <Row label="מטבע" field="currency" file={file}>
                  <select dir="ltr" value={file.currency ?? ''} disabled={busy} aria-label="מטבע"
                    onChange={(e) => edit({ currency: e.target.value })}
                    className="rounded-md border border-input bg-popover px-2 py-1">
                    {!file.currency && <option value="">—</option>}
                    {FUND_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Row>
              </tbody>
            </table>
          </div>

          <div className="grid gap-2">
            {file.warnings.filter((w) => w.code !== 'unconfirmed').map((w) => <Notice key={w.code + w.message} warning={w} />)}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {confirmed ? (
              <span className="text-sm text-success">✓ אושר על ידי {file.confirmed_by}</span>
            ) : (
              <button type="button" disabled={busy || blocking} onClick={() => onEdit(file.file_id, {}, true)}
                className="rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                בדקתי כל ערך מול המסמך — אישור
              </button>
            )}
            {x && <span className="text-xs text-muted-foreground" dir="ltr">{x.provider}/{x.model}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, field, file, children }: { label: string; field: AltField; file: AltFileView; children: React.ReactNode }) {
  const src = file.extraction?.sources[field];
  return (
    <tr className="border-b border-border/40 last:border-0 align-top">
      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{label}</td>
      <td className="px-3 py-2">{children}</td>
      <td className="px-3 py-2"><bdi className="text-foreground/90">{src?.quote ?? <span className="text-muted-foreground">לא מופיע</span>}</bdi></td>
      <td className="hidden px-3 py-2 text-xs text-muted-foreground sm:table-cell"><bdi>{src?.location ?? '—'}</bdi></td>
    </tr>
  );
}

/** A money box that keeps blank (unknown) apart from 0, and saves on leaving the field. */
function NumberBox({ value, busy, label, onSave }: { value: number | null; busy: boolean; label: string; onSave: (v: number | null) => void }) {
  const [text, setText] = useState(show(value));
  useEffect(() => { setText(show(value)); }, [value]);
  return (
    <input inputMode="decimal" dir="ltr" value={text} disabled={busy} aria-label={label} placeholder="לא ידוע"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const clean = text.replace(/[,\s₪$€£]/g, '');
        const v = clean === '' ? null : Number(clean);
        if (v !== null && !Number.isFinite(v)) return;
        if (v !== value) onSave(v);
      }}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      className="w-28 sm:w-36 rounded-md border border-input bg-popover px-2 py-1 text-end tabular-nums" />
  );
}

/** Choose an existing investment, or name a new one. Never decided silently. */
function InvestmentPicker({ file, batch, busy, onEdit }: {
  file: AltFileView; batch: AltBatchView; busy: boolean; onEdit: (e: AltFileEdit) => void;
}) {
  const value = file.investment_id ?? NEW;
  const [name, setName] = useState(file.name ?? '');
  useEffect(() => setName(file.name ?? ''), [file.name]);
  return (
    <span className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <select dir="rtl" value={value} disabled={busy} aria-label="השקעה"
        onChange={(e) => onEdit({ investment_id: e.target.value === NEW ? null : e.target.value })}
        className="w-full max-w-xs rounded-md border border-input bg-popover px-3 py-2 text-sm">
        {batch.investments.map((i) => <option key={i.investment_id} value={i.investment_id}>{i.name}</option>)}
        <option value={NEW}>השקעה חדשה…</option>
      </select>
      {value === NEW && (
        <input value={name} disabled={busy} aria-label="שם ההשקעה החדשה" placeholder="שם ההשקעה"
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() !== (file.name ?? '') && onEdit({ name: name.trim() || null })}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          className="w-full max-w-xs rounded-md border border-input bg-popover px-3 py-2 text-sm" />
      )}
    </span>
  );
}

const TONE: Record<AltWarning['level'], string> = {
  error: 'border-destructive/50 bg-destructive/10 text-destructive',
  warn: 'border-warning/50 bg-warning/10 text-warning',
  info: 'border-border bg-muted/40 text-muted-foreground',
};

export function Notice({ warning }: { warning: AltWarning }) {
  return <p className={`rounded-md border px-3 py-2 text-sm ${TONE[warning.level]}`}>{warning.message}</p>;
}
